import { useEffect, useRef, useState } from "react";
import {
  motion,
  useScroll,
  useTransform,
  useReducedMotion,
  useMotionValueEvent,
} from "framer-motion";

/*
 * Ceramic-white stylus (Apple Pencil look), tip pointing UP.
 * Built at large native size (~1037px tall) so scroll zoom stays crisp.
 * Axial spin is faked credibly for flat divs: every segment uses a LOOPABLE
 * 90deg gradient (start color == end color) tiled at the barrel width (90px),
 * and bgX sweeps one full tile per 360° so the specular highlight travels
 * around the barrel. No masks — the pen is 100% solid at every position.
 */
const barrelSweep = (gradient, bgX) => ({
  backgroundImage: gradient,
  backgroundSize: "90px 100%",
  backgroundRepeat: "repeat-x",
  backgroundPositionX: bgX,
});

const Stylus = ({ floating, engraveOpacity, bgX = 0 }) => (
  <motion.div
    animate={floating ? { y: [0, -14, 0] } : { y: 0 }}
    transition={
      floating
        ? { duration: 6, repeat: Infinity, ease: "easeInOut" }
        : { duration: 0.9, ease: [0.22, 1, 0.36, 1] }
    }
    className="relative"
  >
    {/* Soft white ambience — radial, fades out well before any edge */}
    <div
      className="absolute left-1/2 top-1/2 h-[620px] w-[620px] -translate-x-1/2 -translate-y-1/2 rounded-full"
      style={{
        background:
          "radial-gradient(circle, rgba(255,255,255,0.07) 0%, rgba(255,255,255,0.02) 45%, transparent 68%)",
      }}
      aria-hidden="true"
    />
    <div
      className="relative flex flex-col items-center"
      style={{ filter: "drop-shadow(0 0 14px rgba(255,255,255,0.14))" }}
    >
      {/* Nib — silver metallic cone */}
      <motion.div
        className="h-[95px] w-[90px]"
        style={{
          ...barrelSweep(
            "linear-gradient(90deg,#3f4147 0%,#9ba0a8 28%,#e8eaed 50%,#9ba0a8 72%,#3f4147 100%)",
            bgX
          ),
          clipPath: "polygon(50% 0%, 100% 100%, 0% 100%)",
        }}
      />
      {/* Silver ring */}
      <motion.div
        className="h-[34px] w-[90px]"
        style={barrelSweep(
          "linear-gradient(90deg,#55585e 0%,#c7cbd2 30%,#f3f5f7 50%,#c7cbd2 70%,#55585e 100%)",
          bgX
        )}
      />
      {/* Ceramic body — sharp specular band baked into the loopable gradient */}
      <motion.div
        className="relative h-[850px] w-[90px]"
        style={barrelSweep(
          "linear-gradient(90deg,#9a9ea5 0%,#c9ccd2 12%,#e9ebee 28%,#f8f9fa 42%,#fbfcfd 50%,#f1f3f5 58%,#c9ccd2 80%,#9a9ea5 100%)",
          bgX
        )}
      >
        {/* Engraved marking: NFC + wave — etched into the ceramic */}
        <motion.div
          data-testid="nfc-engraving"
          style={{ opacity: engraveOpacity }}
          className="absolute inset-x-0 top-[105px] flex select-none flex-col items-center"
          aria-hidden="true"
        >
          <span
            className="font-display text-[30px] font-semibold tracking-[0.05em]"
            style={{
              color: "#b3b6bc",
              textShadow:
                "0 -1px 1px rgba(0,0,0,0.38), 0 1px 1px rgba(255,255,255,0.95)",
            }}
          >
            NFC
          </span>
          <span
            className="mt-4 inline-block rotate-90 font-display text-[26px] font-medium"
            style={{
              color: "#b3b6bc",
              letterSpacing: "-2px",
              textShadow:
                "-1px 0 1px rgba(0,0,0,0.32), 1px 0 1px rgba(255,255,255,0.9)",
            }}
          >
            )))
          </span>
        </motion.div>
      </motion.div>
      {/* End cap */}
      <motion.div
        className="h-[58px] w-[90px] rounded-b-[45px]"
        style={barrelSweep(
          "linear-gradient(90deg,#90949b 0%,#e8eaee 30%,#f3f5f7 50%,#e2e4e8 70%,#90949b 100%)",
          bgX
        )}
      />
    </div>
  </motion.div>
);

/* Premium smartphone frame — translucent dark glass, thin silver border */
const Phone = ({ screenY, contentOpacity, onCta }) => (
  <div
    data-testid="nfc-phone"
    className="relative h-[350px] w-[176px] rounded-[32px] p-[3px] md:h-[500px] md:w-[250px] md:rounded-[44px]"
    style={{
      background: "rgba(24,26,30,0.45)",
      border: "1px solid rgba(255,255,255,0.22)",
      backdropFilter: "blur(18px)",
      WebkitBackdropFilter: "blur(18px)",
      boxShadow:
        "0 0 34px rgba(255,255,255,0.08), inset 0 1px 1px rgba(255,255,255,0.18)",
    }}
  >
    {/* side buttons */}
    <div className="absolute -left-[2px] top-16 h-8 w-[3px] rounded-full bg-white/25 md:top-24 md:h-10" aria-hidden="true" />
    <div className="absolute -left-[2px] top-[104px] h-12 w-[3px] rounded-full bg-white/25 md:top-[150px] md:h-16" aria-hidden="true" />
    <div className="absolute -right-[2px] top-20 h-12 w-[3px] rounded-full bg-white/25 md:top-28 md:h-16" aria-hidden="true" />
    <div className="relative h-full w-full overflow-hidden rounded-[29px] bg-black/30 md:rounded-[41px]">
      {/* glass reflection */}
      <div
        className="absolute inset-0"
        style={{
          background:
            "linear-gradient(215deg, rgba(255,255,255,0.08) 0%, transparent 45%)",
        }}
        aria-hidden="true"
      />
      {/* frosted glass screen sliding down like a notification */}
      <motion.div
        data-testid="nfc-screen"
        style={{ y: screenY }}
        className="absolute inset-0 flex flex-col justify-center border-t border-white/20 bg-white/10 px-5 backdrop-blur-2xl md:px-7"
      >
        <motion.div style={{ opacity: contentOpacity }}>
          <p className="text-[8px] font-medium tracking-[0.3em] text-white/50 md:text-[10px]">
            SERVIN LABS · NFC
          </p>
          <h3 className="mt-2 font-display text-[19px] font-semibold leading-[1.05] tracking-tight text-white md:mt-3 md:text-[26px]">
            The Future is a Touch Away
          </h3>
          <p className="mt-2 text-[10px] leading-relaxed text-white/65 md:mt-3 md:text-[13px]">
            Nuestra tecnología NFC es el fin de los menús obsoletos y el inicio
            de una conexión física-digital sin fricciones.
          </p>
          <button
            type="button"
            data-testid="nfc-cta-button"
            onClick={onCta}
            className="mt-4 rounded-full bg-white px-4 py-2.5 text-[10px] font-semibold tracking-wide text-black transition-opacity duration-300 hover:opacity-85 md:mt-5 md:px-6 md:py-3.5 md:text-xs"
          >
            Personaliza tu NFC ahora
          </button>
        </motion.div>
      </motion.div>
      {/* dynamic island */}
      <div className="absolute left-1/2 top-2.5 z-10 h-[16px] w-[56px] -translate-x-1/2 rounded-full border border-white/10 bg-black/80 md:top-3.5 md:h-[22px] md:w-[78px]" aria-hidden="true" />
    </div>
  </div>
);

const ASK_NFC = "¿Cómo puedo implementar tecnología NFC en mi proyecto?";

export default function NFCSection() {
  const ref = useRef(null);
  const reduce = useReducedMotion();
  const [vw, setVw] = useState(() => window.innerWidth);
  const [floating, setFloating] = useState(true);

  useEffect(() => {
    const onResize = () => setVw(window.innerWidth);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  const isMobile = vw < 768;

  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start start", "end end"],
  });

  // Stop levitation as soon as the choreography engages
  useMotionValueEvent(scrollYProgress, "change", (v) => setFloating(v < 0.1));

  const askAngelo = () =>
    window.dispatchEvent(
      new CustomEvent("angelo:ask", { detail: { message: ASK_NFC } })
    );

  const introOpacity = useTransform(scrollYProgress, [0, 0.04, 0.16], [0, 1, 0]);

  // Stage 1: axial 360° — the pen spins on its OWN longitudinal axis.
  // Flat divs can't truly rotateY (edge-on at 90°), so the roll is faked:
  // the barrel texture sweeps exactly one tile (90px) per revolution while
  // a sinusoidal rotateY wobble (±14°, back to 0 at park) sells the 3D.
  const spin = useTransform(scrollYProgress, [0.04, 0.34], [0, 360]);
  const stylusBgX = useTransform(spin, (v) => `${-(v / 360) * 90}px`);
  const stylusRotateY = useTransform(spin, (v) => Math.sin((v * Math.PI) / 180) * 14);
  // Entrance from the background: scale ~0 + opacity 0 deep in the void,
  // grows toward the foreground while spinning, decelerating into park.
  // Pen + glow dim to 30% in the final stretch of the pin so the incoming
  // "Casos de Éxito" white title keeps full contrast during the overlap.
  // SOLID objects: pen, phone and screen carry NO opacity transitions —
  // entrance is scale-only, visibility governed by position/scale alone.
  const scale = useTransform(
    scrollYProgress,
    [0, 0.34, 0.46, 0.5],
    isMobile ? [0.04, 0.7, 1.08, 1.15] : [0.04, 0.7, 1.7, 1.8]
  );
  const penY = useTransform(scrollYProgress, [0.34, 0.5], [0, isMobile ? 190 : 330]);
  const penX = useTransform(
    scrollYProgress,
    [0.34, 0.5],
    [0, isMobile ? -vw * 0.22 : -vw * 0.09]
  );
  // Stage 3: engraving etches in as the pen parks (flat tail keeps it visible)
  const engraveOpacity = useTransform(scrollYProgress, [0.52, 0.6, 1], [0, 1, 1]);

  // Stage 4: phone floats in
  const phoneRest = isMobile ? vw * 0.16 : vw * 0.09;
  const phoneX = useTransform(
    scrollYProgress,
    [0.6, 0.7, 0.78, 0.85],
    [vw * 0.75, phoneRest, phoneRest - (isMobile ? 30 : 55), phoneRest]
  );
  // Stage 5: tilt-touch scan against the engraved zone, then back to vertical
  const phoneRotate = useTransform(scrollYProgress, [0.7, 0.78, 0.85], [0, -12, 0]);
  const pulseOpacity = useTransform(scrollYProgress, [0.75, 0.78, 0.83], [0, 1, 0]);
  const pulseScale = useTransform(scrollYProgress, [0.75, 0.83], [0.5, 2.1]);
  // Stage 6: white screen slides down with the message + CTA.
  // Flat-tail 3-point ranges (see PRD learning): 2-point transforms mirror
  // back after their end keyframe, which blanked the screen while pinned.
  // Content now holds opacity 1 until the pinned section itself exits.
  const screenY = useTransform(scrollYProgress, [0.87, 0.94, 1], ["-102%", "0%", "0%"]);

  const pulseX = isMobile ? -vw * 0.22 + 44 : -vw * 0.09 + 92;
  const pulseY = isMobile ? -85 : -125;

  if (reduce) {
    return (
      <section
        ref={ref}
        data-testid="nfc-section"
        className="relative flex flex-col items-center gap-10 px-6 py-32 text-center"
      >
        <span
          data-testid="nfc-label"
          className="text-[11px] font-medium tracking-[0.45em] text-white/45"
        >
          LA REVOLUCIÓN NFC
        </span>
        <div className="flex flex-col items-center justify-center gap-10 md:flex-row md:gap-20">
          <div data-testid="nfc-stylus" className="flex h-[360px] w-[120px] items-center justify-center">
            <div style={{ transform: "scale(0.3)" }}>
              <Stylus floating={false} engraveOpacity={1} />
            </div>
          </div>
          <Phone screenY={0} contentOpacity={1} onCta={askAngelo} />
        </div>
      </section>
    );
  }

  return (
    <section ref={ref} data-testid="nfc-section" className="relative h-[400vh]">
      <div
        className="sticky top-0 flex h-screen items-center justify-center"
        style={{
          // Safety dissolve (user-prescribed): soft fade at the vertical
          // extremes so the pen NEVER hard-intersects the fixed header bar
          // or the next section's boundary at any viewport height.
          // no-repeat + full size ⇒ overshoot beyond the pinned container
          // melts to transparent instead of hard-cutting.
          WebkitMaskImage:
            "linear-gradient(to bottom, transparent, black 10%, black 90%, transparent)",
          maskImage:
            "linear-gradient(to bottom, transparent, black 10%, black 90%, transparent)",
          WebkitMaskRepeat: "no-repeat",
          maskRepeat: "no-repeat",
          WebkitMaskSize: "100% 100%",
          maskSize: "100% 100%",
        }}
      >
        {/* Intro label */}
        <motion.div
          style={{ opacity: introOpacity }}
          className="absolute top-24 left-0 right-0 flex flex-col items-center gap-4 px-6 text-center md:top-28"
        >
          <span
            data-testid="nfc-label"
            className="text-[11px] font-medium tracking-[0.45em] text-white/45"
          >
            LA REVOLUCIÓN NFC
          </span>
          <span className="text-xs tracking-[0.25em] text-white/30">
            ( DESLIZA PARA ATRAVESAR )
          </span>
        </motion.div>

        {/* Stylus: emerges from the background, axial 360, parks left */}
        <motion.div
          data-testid="nfc-stylus"
          style={{
            x: penX,
            y: penY,
            scale,
            rotateY: stylusRotateY,
            transformPerspective: 1100,
            willChange: "transform",
          }}
        >
          <Stylus floating={floating} engraveOpacity={engraveOpacity} bgX={stylusBgX} />
        </motion.div>

        {/* Phone: floats in, tilt-touches the NFC zone, reveals the white screen */}
        <motion.div
          style={{
            x: phoneX,
            rotate: phoneRotate,
            transformOrigin: "50% 88%",
            willChange: "transform",
          }}
          className="absolute left-1/2 top-1/2 -ml-[88px] -mt-[175px] md:-ml-[125px] md:-mt-[250px]"
        >
          <Phone screenY={screenY} contentOpacity={1} onCta={askAngelo} />
        </motion.div>

        {/* Scan pulse at the contact point */}
        <motion.div
          data-testid="nfc-pulse"
          style={{ x: pulseX, y: pulseY, opacity: pulseOpacity, scale: pulseScale }}
          className="pointer-events-none absolute left-1/2 top-1/2 -ml-8 -mt-8 h-16 w-16 rounded-full"
          aria-hidden="true"
        >
          <div
            className="h-full w-full rounded-full"
            style={{
              background:
                "radial-gradient(circle, rgba(255,255,255,0.9) 0%, rgba(255,255,255,0.25) 40%, transparent 70%)",
            }}
          />
        </motion.div>
      </div>
    </section>
  );
}
